import React, { Fragment, useMemo, useState } from 'react'
import { useQueryParams } from 'use-query-params'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ContentLayout } from '@app/layouts'
import { ExtendedTable, PermissionWrapper, SubheaderToolbarDivider, TableSettingsToolbar } from '@app/components'
import { Show } from '@app/components/util'
import { LntButton } from '@app/components/base'
import { RecommendationsDrawer } from '@app/drawers'
import { ConfirmModal } from '@app/modals'

import { useNotification, usePaginationQueryParams, useTableSearch, useTableSetting, useToggle } from '@app/hooks'
import { useContractorEditableColumn, useScheduleEditableColumns } from '@app/pages/MonitoringLaunchPage/hooks'
import { ContractorsChangeModal, ScheduleChangeModal } from 'src/pages/MonitoringLaunchPage/ui'

import { getMonitoringTotalCreatingTasks } from '@app/api/routes/monitoringLists'
import {
  patchTaskTotalRunParameters,
  postMonitoringTasksTotal,
  postMonitoringTasksTotalAll
} from '@app/api/routes/monitoringTasks'

import { SIZE, BUTTON_TYPE } from '@app/constants'
import { MONITORINGS_CREATING_TASKS_KEY } from '@app/constants/queryKeys'
import { PERMISSION } from '@app/constants/permission'
import { TABLE_SETTING_KEYS } from '@app/constants/tableSettingKeys'

import { getColumns, adaptTotalMonitoringDataSource, adaptPostMonitoringTasksTotalAllData } from './lib'
import { useRecommendationColumn, useCompetitorsEditableColumn } from './hooks'
import { TopFilters } from './ui'
import { topFiltersQuerySchema, tableSearchQuerySchema, columnsSearchSchema } from './configs'

import type { FiltersParams } from '@app/types/filter'
import type { GetMonitoringCreatingTotalTasksParams } from '@app/api/routes/monitoringLists/types'
import type { Key, ReactNode } from 'react'
import type { ScheduleData, TotalMonitoringDataSource } from './types'
import type { TaskTotalRunParametersData } from '@app/api/routes/monitoringTasks/types'

const columns = getColumns()

export const TotalMonitoringTab: React.FC = () => {
  const queryClient = useQueryClient()
  const notification = useNotification()

  const { paginationParams, pagination, setPagination } = usePaginationQueryParams()
  const [queryFilters, setQueryFilters] = useQueryParams(topFiltersQuerySchema)

  const [selectedDataRecords, setSelectedDataRecords] = useState<TotalMonitoringDataSource[]>([])
  const [rowSelectionKeys, setRowSelectionKeys] = useState<number[]>([])

  const totalMonitoringParams = useMemo(
    () => ({ ...paginationParams, ...queryFilters }),
    [paginationParams, queryFilters]
  ) as GetMonitoringCreatingTotalTasksParams

  const { data: totalMonitoringDataSource, isLoading: isMonitoringsTotalDataLoading } = useQuery({
    queryKey: [MONITORINGS_CREATING_TASKS_KEY.monitoringsTotalCreatingTasksItems, totalMonitoringParams],
    queryFn: async () => await getMonitoringTotalCreatingTasks(totalMonitoringParams),
    select: monitoringTotalCreatingTasksData => adaptTotalMonitoringDataSource(monitoringTotalCreatingTasksData)
  })

  const patchTotalCreatingTasksItemMutation = useMutation(patchTaskTotalRunParameters, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [MONITORINGS_CREATING_TASKS_KEY.monitoringsTotalCreatingTasksItems]
      })

      notification.success({
        message: 'Сплошной мониторинг изменен',
        description: 'Успешное изменение сплошного мониторинга'
      })

      recommendationsDrawerToggle.setUntoggled()
    }
  })

  const postMonitoringTasksTotalMutation = useMutation({
    mutationFn: postMonitoringTasksTotal,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [MONITORINGS_CREATING_TASKS_KEY.monitoringsTotalCreatingTasksItems]
      })

      notification.success({
        message: 'Запуск выбранных мониторингов',
        description: 'Успешный запуск выбранных мониторингов'
      })
    }
  })

  const postMonitoringTasksTotalAllMutation = useMutation({
    mutationFn: postMonitoringTasksTotalAll,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [MONITORINGS_CREATING_TASKS_KEY.monitoringsTotalCreatingTasksItems]
      })

      notification.success({
        message: 'Запуск всех мониторингов',
        description: 'Успешный запуск всех мониторингов'
      })
    }
  })

  const handleScheduleChange = async (scheduleData: ScheduleData): Promise<void> => {
    await patchTotalCreatingTasksItemMutation.mutateAsync({
      runParametersIds: rowSelectionKeys,
      ...scheduleData
    })

    scheduleChangeModalToggle.setUntoggled()
  }

  const handleContractorsChange = async (contractorId: number): Promise<void> => {
    await patchTotalCreatingTasksItemMutation.mutateAsync({
      runParametersIds: rowSelectionKeys,
      contractorId
    })

    contractorsChangeModalToggle.setUntoggled()
  }

  const handleMonitoringStartChecked = async (): Promise<void> => {
    postMonitoringTasksTotalMutation.mutate({ runParametersIds: rowSelectionKeys })
  }

  const handleMonitoringStartAll = async (): Promise<void> => {
    const postMonitoringTasksTotalAllData = adaptPostMonitoringTasksTotalAllData(queryFilters)
    postMonitoringTasksTotalAllMutation.mutate(postMonitoringTasksTotalAllData)
    monitoringStartAllModalToggle.setUntoggled()
  }

  const scheduleChangeModalToggle = useToggle()
  const contractorsChangeModalToggle = useToggle()
  const recommendationsDrawerToggle = useToggle()
  const monitoringStartAllModalToggle = useToggle()

  const handleFiltersChange = (filters: FiltersParams): void => {
    setQueryFilters(filters)
  }

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handleSelectedRowChange = (keys: Key[], selectedRecords: TotalMonitoringDataSource[]): void => {
    setRowSelectionKeys(keys.map(Number))

    setSelectedDataRecords(selectedRecords)
  }

  const handleChangeContractorCell = (contractorId: number | null, recordKey: number[]): void => {
    patchTotalCreatingTasksItemMutation.mutate({ runParametersIds: recordKey, contractorId })
  }

  const handleChangeCompetitorsCell = (competitorIds: string[], recordKey: number[]): void => {
    patchTotalCreatingTasksItemMutation.mutate({ runParametersIds: recordKey, competitorIds })
  }

  const columnsWithContractors = useContractorEditableColumn<TotalMonitoringDataSource>({
    columns,
    patchCreatingTasks: handleChangeContractorCell
  })

  const columnsWithCompetitors = useCompetitorsEditableColumn({
    columns: columnsWithContractors,
    patchCreatingTasks: handleChangeCompetitorsCell
  })

  const columnsWithScheduleEditableColumns = useScheduleEditableColumns<TotalMonitoringDataSource>({
    columns: columnsWithCompetitors,
    patchCreatingTasks: data => patchTotalCreatingTasksItemMutation.mutate(data)
  })

  const { columnRunParametersId, columnRecommendationData, columnsWithRecommendation } = useRecommendationColumn({
    columns: columnsWithScheduleEditableColumns,
    onClickCell: recommendationsDrawerToggle.setToggled
  })

  const {
    items,
    defaultItems,
    columns: modifiedColumns,
    onModify
  } = useTableSetting(TABLE_SETTING_KEYS.totalMonitoringTab, columnsWithRecommendation)

  const {
    columns: columnsWithSearchTool,
    filteredDataSource: filteredMonitoringCreatingTotalTasksDataSource,
    isSearchToolOpened,
    toggleSearchTool
  } = useTableSearch({
    columns: modifiedColumns,
    dataSource: totalMonitoringDataSource?.dataSource,
    columnsSearchSchema,
    tableSearchQuerySchema
  })

  const toolbar: ReactNode = [
    <Show
      key='date-change_monitorings-turn-on_monitorings-turn-off-show'
      condition={ selectedDataRecords.length > 0 }
    >
      <PermissionWrapper requiredPermissions={ [PERMISSION.startRestartMonitoringTask] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ contractorsChangeModalToggle.setToggled }
        >
          Изменить подрядчика
        </LntButton>
      </PermissionWrapper>

      <PermissionWrapper requiredPermissions={ [PERMISSION.startRestartMonitoringTask] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ scheduleChangeModalToggle.setToggled }
        >
          Изменить даты
        </LntButton>
      </PermissionWrapper>

      <SubheaderToolbarDivider />
    </Show>,

    <Show
      key='show-launch-checked-button'
      condition={ selectedDataRecords.length >= 1 }
    >
      <PermissionWrapper requiredPermissions={ [PERMISSION.startRestartMonitoringTask] }>
        <LntButton
          type={ BUTTON_TYPE.secondary }
          size={ SIZE.small }
          onClick={ handleMonitoringStartChecked }
        >
          Запустить выбранные
        </LntButton>
      </PermissionWrapper>
    </Show>,

    <Fragment key='launch-all-button'>
      <PermissionWrapper requiredPermissions={ [PERMISSION.startRestartMonitoringTask] }>
        <LntButton
          type={ BUTTON_TYPE.primary }
          size={ SIZE.small }
          loading={ postMonitoringTasksTotalAllMutation.isLoading }
          onClick={ monitoringStartAllModalToggle.setToggled }
        >
          Запустить все
        </LntButton>

        <SubheaderToolbarDivider />
      </PermissionWrapper>
    </Fragment>,

    <TableSettingsToolbar
      key='table-settings-toolbar'
      tableSettingButtonProps={ { items, defaultItems, onAccept: onModify } }
      tableSearchButtonProps={ { active: isSearchToolOpened, onClick: toggleSearchTool } }
    />
  ]

  const handleOnSubmitRecommendationsDrawer = (data: TaskTotalRunParametersData): void => {
    patchTotalCreatingTasksItemMutation.mutate({
      referralRecommendation: data,
      runParametersIds: [columnRunParametersId]
    })
  }

  return (
    <>
      <ContentLayout>
        <TopFilters
          onSearch={ handleFiltersChange }
          initialFilters={ queryFilters as FiltersParams }
        />

        <ExtendedTable
          loading={ isMonitoringsTotalDataLoading }
          columns={ columnsWithSearchTool }
          dataSource={ filteredMonitoringCreatingTotalTasksDataSource }
          toolbar={ toolbar }
          rowSelection={ {
            selectedRowKeys: rowSelectionKeys,
            onChange: handleSelectedRowChange
          } }
          pagination={ {
            ...pagination,
            total: totalMonitoringDataSource?.count,
            onChange: handlePaginationChange
          } }
        />
      </ContentLayout>

      <ScheduleChangeModal
        open={ scheduleChangeModalToggle.value }
        onCancel={ scheduleChangeModalToggle.setUntoggled }
        onScheduleChange={ handleScheduleChange }
      />

      <ContractorsChangeModal
        open={ contractorsChangeModalToggle.value }
        onCancel={ contractorsChangeModalToggle.setUntoggled }
        onContractorsChange={ handleContractorsChange }
      />

      <RecommendationsDrawer
        open={ recommendationsDrawerToggle.value }
        onClose={ recommendationsDrawerToggle.setUntoggled }
        recommendation={ columnRecommendationData }
        onSubmit={ handleOnSubmitRecommendationsDrawer }
        isLoading={ patchTotalCreatingTasksItemMutation.isLoading }
      />

      <ConfirmModal
        title='Вы уверены, что хотите произвести массовый запуск задач?'
        cancelText='Отмена'
        open={ monitoringStartAllModalToggle.value }
        onCancel={ monitoringStartAllModalToggle.setUntoggled }
        onConfirm={ handleMonitoringStartAll }
      />
    </>
  )
}
